import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { Subject, catchError, finalize, of, switchMap, takeUntil } from 'rxjs';
import { LocationService } from '../services/location.service';
import { EmployeeService } from '../employee/services/employee.service';
import { LoggerService } from '../services/logger.service';
import { AmplitudeService } from '../services/amplitude.service';
import { DateRangePickerComponent } from '../shared/components/wi-date-range-picker/wi-date-range-picker.component';
import {
  CheckboxMultiDropdownComponent,
  CheckboxMultiDropdownOption,
} from '../shared/components/checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import { ReportsService } from './reports.service';
import { ShiftReportData } from './models/shift-report.model';
import { CashReportData } from './models/cash-report.model';
import { ShiftReportResultsComponent } from './components/shift-report-results/shift-report-results.component';
import { CashReportResultsComponent } from './components/cash-report-results/cash-report-results.component';
import { GeneralSalesReportResultsComponent } from './components/general-sales-report-results/general-sales-report-results.component';
import {
  GeneralSalesFilterOptions,
  GeneralSalesFilterOptionsRequest,
  GeneralSalesFilters,
  GeneralSalesReportData,
  emptyGeneralSalesFilters,
} from './models/general-sales-report.model';
import {
  DATE_PRESET_OPTIONS,
  DEFAULT_DATE_PRESET,
  DatePresetId,
  resolveDatePreset,
} from './helpers/date-presets.helper';
import { ScheduledReportsListComponent } from './components/scheduled-reports-list/scheduled-reports-list.component';
import {
  getGsrIncludeCancelledOrdersPreference,
  resolvePreferenceUserId,
  setGsrIncludeCancelledOrdersPreference,
} from '../shared/utils/user-preferences.util';

export type ReportsTab = 'view' | 'scheduled';

export type ReportType = 'shift' | 'cash' | 'general-sales';

/** Primary generate-report filter popovers that must stay mutually exclusive. */
type ReportsPrimaryFilter =
  'reportType' | 'location' | 'employees' | 'datePreset' | 'date' | 'startDate' | 'endDate';

/** An active filter shown as a removable chip beneath the controls. */
export interface GeneralSalesFilterChip {
  key: keyof GeneralSalesFilters | 'compare';
  id: string;
  label: string;
  /** True when the filter narrows only part of the report (rewash tables). */
  sectionScoped?: boolean;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    DateRangePickerComponent,
    CheckboxMultiDropdownComponent,
    ShiftReportResultsComponent,
    CashReportResultsComponent,
    GeneralSalesReportResultsComponent,
    ScheduledReportsListComponent,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReportsComponent implements OnInit, OnDestroy {
  private readonly locationService = inject(LocationService);
  private readonly employeeService = inject(EmployeeService);
  private readonly reportsService = inject(ReportsService);
  private readonly logger = inject(LoggerService);
  private readonly amplitude = inject(AmplitudeService);
  private readonly destroy$ = new Subject<void>();
  private readonly employeesReload$ = new Subject<string[]>();
  /**
   * Filter-option reloads go through a subject so switchMap can cancel the
   * in-flight request. Without it a slower response for an earlier window could
   * land last and replace the current window's options, and
   * dropFiltersWithNoOption would then prune selections against a stale list.
   */
  private readonly filterOptionsReload$ = new Subject<GeneralSalesFilterOptionsRequest>();

  @ViewChild('reportTypeSelect') reportTypeSelect?: MatSelect;
  @ViewChild('datePresetSelect') datePresetSelect?: MatSelect;
  @ViewChild('locationDropdown') locationDropdown?: CheckboxMultiDropdownComponent;
  @ViewChild('employeesDropdown') employeesDropdown?: CheckboxMultiDropdownComponent;
  @ViewChild('reportDatePicker') reportDatePicker?: DateRangePickerComponent;
  @ViewChild('reportStartDatePicker') reportStartDatePicker?: DateRangePickerComponent;
  @ViewChild('reportEndDatePicker') reportEndDatePicker?: DateRangePickerComponent;

  readonly tabs: { id: ReportsTab; label: string }[] = [
    { id: 'view', label: 'View reports' },
    { id: 'scheduled', label: 'Scheduled report' },
  ];

  readonly reportTypeOptions: { label: string; value: ReportType }[] = [
    { label: 'Shift Report', value: 'shift' },
    { label: 'Cash Report', value: 'cash' },
    { label: 'General Sales Report', value: 'general-sales' },
  ];

  activeTab: ReportsTab = 'view';
  reportType: ReportType = 'shift';
  selectedLocationIds: string[] = [];
  selectedEmployeeIds: string[] = [];
  selectedDate: Date | null = new Date();
  selectedStartDate: Date | null = new Date();
  selectedEndDate: Date | null = new Date();

  locationOptions: CheckboxMultiDropdownOption[] = [];
  employeeOptions: CheckboxMultiDropdownOption[] = [];

  locationsLoading = false;
  employeesLoading = false;
  reportLoading = false;
  reportError: string | null = null;
  shiftReport: ShiftReportData | null = null;
  shiftExportDate = '';
  shiftExportLocationIds: string[] = [];
  shiftExportEmployeeIds: string[] = [];
  cashReport: CashReportData | null = null;
  cashExportStartDate = '';
  cashExportEndDate = '';
  cashExportEmployeeIds: string[] | undefined;
  generalSalesReport: GeneralSalesReportData | null = null;
  /**
   * Opt-in, not on by default.
   *
   * The comparison doubles the report's database work: svc-analytics runs a
   * second full fan-out over the previous window, and a window with no orders
   * behind it still gets scanned in full. On a 267-day production report that
   * second pass is most of a 1.39 s response, paid on every run whether or not
   * anyone reads the deltas. Anyone who wants them ticks the box.
   */
  compareToPreviousPeriod = false;

  readonly datePresetOptions = DATE_PRESET_OPTIONS;
  selectedDatePreset: DatePresetId = DEFAULT_DATE_PRESET;

  generalSalesFilters: GeneralSalesFilters = emptyGeneralSalesFilters();
  filterOptions: GeneralSalesFilterOptions | null = null;
  filterOptionsLoading = false;
  filtersPanelOpen = false;
  exportingCsv = false;
  exportingXlsx = false;
  exportingPdf = false;
  /**
   * Whether General Sales exports carry the cancelled-order list. Off by
   * default: system auto-cancels can add hundreds of rows to the file. The
   * Voids count is unaffected either way. The screen always loads the list.
   */
  includeCancelledOrdersInExport = false;

  ngOnInit(): void {
    const preferenceUserId = resolvePreferenceUserId();
    if (preferenceUserId) {
      this.includeCancelledOrdersInExport =
        getGsrIncludeCancelledOrdersPreference(preferenceUserId) ?? false;
    }

    this.employeesReload$
      .pipe(
        switchMap(locationIds => {
          this.employeesLoading = true;
          return this.employeeService
            .loadEmployees(1, 1000, undefined, {
              status: 'active',
              locations: locationIds.length > 0 ? locationIds : undefined,
            })
            .pipe(
              catchError(err => {
                this.logger.error('Reports: failed to load employees', err);
                return of({ employees: [] });
              }),
              finalize(() => {
                this.employeesLoading = false;
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        this.employeeOptions = (res.employees ?? []).map(emp => ({
          id: emp.id,
          name: `${emp.first_name} ${emp.last_name}`.trim(),
        }));
        const validIds = new Set(this.employeeOptions.map(opt => opt.id));
        this.selectedEmployeeIds = this.selectedEmployeeIds.filter(id => validIds.has(id));
      });

    this.filterOptionsReload$
      .pipe(
        switchMap(request => {
          this.filterOptionsLoading = true;
          return this.reportsService.getGeneralSalesFilterOptions(request).pipe(
            catchError(err => {
              this.logger.error('Reports: failed to load general sales filter options', err);
              // Filters are an enhancement; the report still runs unfiltered.
              return of(null);
            }),
            finalize(() => {
              this.filterOptionsLoading = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(options => {
        this.filterOptions = options;
        if (options) {
          this.dropFiltersWithNoOption(options);
        } else {
          // Every one of these controls draws its choices from the response that
          // just failed, so the user can neither see nor clear what is selected —
          // yet the selections would still travel with the next report request
          // and silently filter out valid sales. Run unfiltered instead, which is
          // what the failed load already assumes.
          this.generalSalesFilters = emptyGeneralSalesFilters();
        }
      });

    this.loadLocations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.employeesReload$.complete();
    this.filterOptionsReload$.complete();
  }

  setActiveTab(tab: ReportsTab): void {
    this.activeTab = tab;
  }

  /**
   * Keep Report Type / Location / Employees / Date popovers mutually exclusive.
   * Custom triggers stopPropagation, so sibling outside-click handlers alone are not enough.
   */
  onPrimaryFilterOpened(source: ReportsPrimaryFilter, isOpen: boolean): void {
    if (!isOpen) {
      return;
    }
    this.closeOtherPrimaryFilters(source);
  }

  private closeOtherPrimaryFilters(except: ReportsPrimaryFilter): void {
    if (except !== 'reportType') {
      this.reportTypeSelect?.close();
    }
    if (except !== 'datePreset') {
      this.datePresetSelect?.close();
    }
    if (except !== 'location') {
      this.locationDropdown?.close();
    }
    if (except !== 'employees') {
      this.employeesDropdown?.close();
    }
    if (except !== 'date') {
      this.reportDatePicker?.close();
    }
    if (except !== 'startDate') {
      this.reportStartDatePicker?.close();
    }
    if (except !== 'endDate') {
      this.reportEndDatePicker?.close();
    }
  }

  onReportTypeChange(): void {
    this.shiftReport = null;
    this.cashReport = null;
    this.generalSalesReport = null;
    this.reportError = null;
    // The General Sales Report defaults to the current month (PRD §5).
    if (this.reportType === 'general-sales') {
      this.applyCurrentMonthRange();
      this.trackReport('opened');
    }
  }

  onIncludeCancelledOrdersInExportChange(include: boolean): void {
    this.includeCancelledOrdersInExport = include;
    const preferenceUserId = resolvePreferenceUserId();
    if (preferenceUserId) {
      setGsrIncludeCancelledOrdersPreference(preferenceUserId, include);
    }
  }

  onCompareChange(enabled: boolean): void {
    this.compareToPreviousPeriod = enabled;
    if (enabled) {
      this.trackReport('comparison_enabled');
    }
  }

  /** General Sales Report filters by location only; employees are not one of its dimensions. */
  get usesEmployeeFilter(): boolean {
    return this.reportType !== 'general-sales';
  }

  get usesDateRange(): boolean {
    return this.reportType === 'cash' || this.reportType === 'general-sales';
  }

  /** PRD §5: the report opens on the current month. */
  private applyCurrentMonthRange(): void {
    this.selectedDatePreset = DEFAULT_DATE_PRESET;
    this.applyDatePreset(DEFAULT_DATE_PRESET);
  }

  onDatePresetChange(preset: DatePresetId): void {
    this.selectedDatePreset = preset;
    this.applyDatePreset(preset);
    this.trackReport('date_range_changed', { preset });
  }

  /** 'custom' leaves the pickers alone so the user can choose their own dates. */
  private setDatePresetRange(preset: DatePresetId): boolean {
    const range = resolveDatePreset(preset, new Date(), this.reportTimezone());
    if (!range) {
      return false;
    }
    this.selectedStartDate = range.start;
    this.selectedEndDate = range.end;
    return true;
  }

  private applyDatePreset(preset: DatePresetId): void {
    if (this.setDatePresetRange(preset)) {
      this.reloadFilterOptions();
    }
  }

  /** PRD §30.10: nothing to export means the control is disabled, not a broken file. */
  get canExportGeneralSales(): boolean {
    return (
      this.reportType === 'general-sales' &&
      this.generalSalesReport !== null &&
      !this.generalSalesHasNoData &&
      !this.exportingCsv &&
      !this.exportingXlsx &&
      !this.exportingPdf
    );
  }

  onExportGeneralSales(format: 'csv' | 'xlsx' | 'pdf'): void {
    if (!this.canExportGeneralSales || !this.selectedStartDate || !this.selectedEndDate) {
      return;
    }

    const body = {
      start_date: this.toDateString(this.selectedStartDate),
      end_date: this.toDateString(this.selectedEndDate),
      location_ids: this.selectedLocationIds,
      timezone: this.reportTimezone(),
      compare: this.compareToPreviousPeriod,
      filters: this.generalSalesFilters,
      include_cancelled_orders: this.includeCancelledOrdersInExport,
    };

    if (format === 'csv') {
      this.exportingCsv = true;
    } else if (format === 'xlsx') {
      this.exportingXlsx = true;
    } else {
      this.exportingPdf = true;
    }
    this.trackReport(`${format}_export_clicked`, {
      date_range: `${body.start_date}..${body.end_date}`,
      include_cancelled_orders: body.include_cancelled_orders,
    });

    const download = {
      csv: () => this.reportsService.downloadGeneralSalesCsv(body),
      xlsx: () => this.reportsService.downloadGeneralSalesXlsx(body),
      pdf: () => this.reportsService.downloadGeneralSalesPdf(body),
    };
    const request$ = download[format]();

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.exportingCsv = false;
          this.exportingXlsx = false;
          this.exportingPdf = false;
        })
      )
      .subscribe({
        next: blob =>
          this.saveBlob(
            blob,
            `general-sales-report_${body.start_date}_to_${body.end_date}.${format}`
          ),
        error: err => {
          this.logger.error(`Reports: general sales ${format} export failed`, err);
          this.reportError = `Could not export the report as ${format.toUpperCase()}. Please try again.`;
        },
      });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Release the object URL once the browser has taken the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  toggleFiltersPanel(): void {
    this.filtersPanelOpen = !this.filtersPanelOpen;
  }

  /** Custom is the only preset that needs the date pickers on screen. */
  get showCustomDateRange(): boolean {
    return this.reportType !== 'general-sales' || this.selectedDatePreset === 'custom';
  }

  /** Drives the badge on the Filters button. Comparison counts as an active option. */
  get activeFilterCount(): number {
    const f = this.generalSalesFilters;
    return (
      (f.customer_type === 'all' ? 0 : 1) +
      f.package_ids.length +
      f.payment_methods.length +
      f.discount_types.length +
      f.rewash_reasons.length +
      f.employee_ids.length +
      (this.compareToPreviousPeriod ? 1 : 0)
    );
  }

  /**
   * Active filters as removable chips, so what is in force stays visible once
   * the panel is closed.
   */
  get activeFilterChips(): GeneralSalesFilterChip[] {
    const chips: GeneralSalesFilterChip[] = [];
    const f = this.generalSalesFilters;
    const options = this.filterOptions;

    const nameOf = (list: { id: string; name: string }[] | undefined, id: string): string =>
      list?.find(option => option.id === id)?.name ?? id;

    if (this.compareToPreviousPeriod) {
      chips.push({ key: 'compare', id: 'compare', label: 'vs previous period' });
    }
    if (f.customer_type !== 'all') {
      chips.push({
        key: 'customer_type',
        id: f.customer_type,
        label: nameOf(options?.customerTypes, f.customer_type),
      });
    }
    for (const id of f.package_ids) {
      chips.push({ key: 'package_ids', id, label: nameOf(options?.packages, id) });
    }
    for (const id of f.payment_methods) {
      chips.push({ key: 'payment_methods', id, label: nameOf(options?.paymentMethods, id) });
    }
    for (const id of f.discount_types) {
      chips.push({ key: 'discount_types', id, label: nameOf(options?.discountTypes, id) });
    }
    for (const id of f.rewash_reasons) {
      chips.push({
        key: 'rewash_reasons',
        id,
        label: nameOf(options?.rewashReasons, id),
        sectionScoped: true,
      });
    }
    for (const id of f.employee_ids) {
      chips.push({
        key: 'employee_ids',
        id,
        label: nameOf(options?.rewashEmployees, id),
        sectionScoped: true,
      });
    }
    return chips;
  }

  removeFilterChip(chip: GeneralSalesFilterChip): void {
    if (chip.key === 'compare') {
      this.compareToPreviousPeriod = false;
      return;
    }
    if (chip.key === 'customer_type') {
      this.onGeneralSalesFilterChange('customer_type', 'all');
      return;
    }
    const current = this.generalSalesFilters[chip.key] as string[];
    this.onGeneralSalesFilterChange(chip.key, current.filter(id => id !== chip.id) as never);
  }

  onGeneralSalesFilterChange<K extends keyof GeneralSalesFilters>(
    key: K,
    value: GeneralSalesFilters[K]
  ): void {
    this.generalSalesFilters = { ...this.generalSalesFilters, [key]: value };
  }

  onClearGeneralSalesFilters(): void {
    this.generalSalesFilters = emptyGeneralSalesFilters();
  }

  /** PRD §26: a period with no qualifying washes gets the no-data state, not an empty report. */
  get generalSalesHasNoData(): boolean {
    if (this.reportType !== 'general-sales' || this.generalSalesReport === null) {
      return false;
    }
    const { volume } = this.generalSalesReport;
    return (
      volume.totalCarsWashed === 0 &&
      // Gross covers every paid order type since CWS-1368, so a period that sold
      // only membership plans or prepaid books has gross > 0 and is not empty.
      volume.grossSales === 0 &&
      // CWS-1242: cancelled orders are excluded from volume and gross on purpose
      // (no car was washed), but they are still reported as voids in Revenue
      // Leakage. A period whose only activity was a cancellation has 0 cars and
      // 0 gross, so without this the whole report — including the void that
      // explains where the order went — was replaced by "no data".
      this.adjustmentActivityCount === 0 &&
      // A report that still has members to show is not an empty report. Membership
      // is the one section that survives a period with no sales: plans billed and
      // washes redeemed are period activity, and the base counts are a live
      // snapshot. Without this the 985-member tiles were replaced by "no data".
      this.membershipHasNothingToShow
    );
  }

  /**
   * Voids and refunds recorded in the period (PRD §16). Counted, not summed: a
   * fully comped order is voided for $0 and still has to be reported.
   */
  private get adjustmentActivityCount(): number {
    return this.generalSalesReport?.leakage?.adjustmentTotalCount ?? 0;
  }

  /**
   * True when the membership section would render nothing worth reading.
   *
   * Two different things have to be zero. Plan sales, billed revenue and
   * redeemed washes are period activity, and are counted separately because a
   * comped plan bills $0 and still sold. `base.ending` is not period activity at
   * all — `subscriptions` carries no location_id, so it is a tenant-wide "live
   * right now" snapshot (the API flags this as `baseIsTenantWide`) and it is the
   * number behind the membership KPI tiles. A tenant with live members therefore
   * never gets the empty state, which is the intended trade: those tiles are
   * real, and blanking them to say "no data" was the bug.
   */
  private get membershipHasNothingToShow(): boolean {
    const membership = this.generalSalesReport?.membership;
    if (!membership) {
      return true;
    }
    return (
      (membership.planSaleCount ?? 0) === 0 &&
      (membership.revenueBilled ?? 0) === 0 &&
      (membership.washesRedeemed ?? 0) === 0 &&
      (membership.base?.ending ?? 0) === 0
    );
  }

  /** PRD §26: some sections can be unavailable while the rest of the report is sound. */
  get generalSalesPartialData(): boolean {
    const report = this.generalSalesReport;
    if (!report || this.generalSalesHasNoData) {
      return false;
    }
    return report.membership.available === false;
  }

  retryGeneralSalesReport(): void {
    this.onFilter();
  }

  get hasActiveGeneralSalesFilters(): boolean {
    const f = this.generalSalesFilters;
    return (
      f.customer_type !== 'all' ||
      f.package_ids.length > 0 ||
      f.payment_methods.length > 0 ||
      f.discount_types.length > 0 ||
      f.rewash_reasons.length > 0 ||
      f.employee_ids.length > 0
    );
  }

  /**
   * Filter options come from the server for the selected window, so a control
   * never offers a value that would return nothing.
   */
  private reloadFilterOptions(): void {
    if (this.reportType !== 'general-sales' || !this.selectedStartDate || !this.selectedEndDate) {
      return;
    }

    this.filterOptionsReload$.next({
      start_date: this.toDateString(this.selectedStartDate),
      end_date: this.toDateString(this.selectedEndDate),
      location_ids: this.selectedLocationIds,
      timezone: this.reportTimezone(),
    });
  }

  /** Drop selections that the new window no longer offers, so a stale id cannot silently filter everything out. */
  private dropFiltersWithNoOption(options: GeneralSalesFilterOptions): void {
    const keep = (selected: string[], available: { id: string }[]): string[] => {
      const ids = new Set(available.map(option => option.id));
      return selected.filter(id => ids.has(id));
    };

    this.generalSalesFilters = {
      ...this.generalSalesFilters,
      package_ids: keep(this.generalSalesFilters.package_ids, options.packages),
      payment_methods: keep(this.generalSalesFilters.payment_methods, options.paymentMethods),
      discount_types: keep(this.generalSalesFilters.discount_types, options.discountTypes),
      rewash_reasons: keep(this.generalSalesFilters.rewash_reasons, options.rewashReasons),
      employee_ids: keep(this.generalSalesFilters.employee_ids, options.rewashEmployees),
    };
  }

  onDateChange(date: Date | null): void {
    this.selectedDate = date;
  }

  // Picking a date by hand never moves the Reporting period selector off
  // "Custom Range". The pickers only render while the selector reads Custom, so
  // snapping it to a matching preset — start = end = today reads as "Today" —
  // hid them after the first click and the user had to choose Custom again to
  // pick the end date (CWS-1368).
  onStartDateChange(date: Date | null): void {
    this.selectedStartDate = date;
    if (
      this.selectedStartDate &&
      this.selectedEndDate &&
      this.selectedEndDate.getTime() < this.selectedStartDate.getTime()
    ) {
      this.selectedEndDate = new Date(this.selectedStartDate);
    }
    // A preset reloads these; picking dates by hand has to as well, or the
    // controls keep offering the previous window's packages and reasons and a
    // stale id silently filters out valid sales.
    this.reloadFilterOptions();
  }

  onEndDateChange(date: Date | null): void {
    this.selectedEndDate = date;
    this.reloadFilterOptions();
  }

  onLocationIdsChange(ids: string[]): void {
    this.selectedLocationIds = [...new Set(ids)];
    // The new location may keep time in a different zone, so a preset range
    // built for the old one no longer means the same days. Set the range only —
    // reloadFilterOptions() below covers the refresh.
    if (this.selectedDatePreset !== 'custom') {
      this.setDatePresetRange(this.selectedDatePreset);
    }
    this.selectedEmployeeIds = [];
    // Drop stale options immediately so cash generation cannot use the previous location's employees.
    this.employeeOptions = [];
    this.employeesLoading = true;
    this.reloadEmployees();
    this.reloadFilterOptions();
    if (this.reportType === 'general-sales') {
      this.trackReport('location_filter_changed', { locations: this.selectedLocationIds.length });
    }
  }

  onEmployeeIdsChange(ids: string[]): void {
    this.selectedEmployeeIds = [...new Set(ids)];
  }

  onFilter(): void {
    // Block only when a location change cleared options and the reload is still in flight.
    // The General Sales Report does not filter by employee, so it never waits on that reload.
    if (this.usesEmployeeFilter && this.employeesLoading && this.employeeOptions.length === 0) {
      return;
    }
    if (this.reportType === 'general-sales') {
      this.loadGeneralSalesReport();
      return;
    }
    if (this.reportType === 'cash') {
      this.loadCashReport();
      return;
    }
    this.loadShiftReport();
  }

  /**
   * Clear the filters, not the page.
   *
   * This used to reset `reportType` to 'shift' as well, so clearing filters on
   * the General Sales Report threw the user back to the Shift Report and lost
   * the report they were working on. Which report is on screen is a choice the
   * user made, not a filter.
   */
  onClear(): void {
    this.selectedLocationIds = [];
    this.selectedEmployeeIds = [];
    this.selectedDate = new Date();
    this.shiftReport = null;
    this.cashReport = null;
    this.generalSalesReport = null;
    // Back to the opt-in default, not on: Clear must not leave a filter set
    // that the user never chose.
    this.compareToPreviousPeriod = false;
    this.selectedDatePreset = DEFAULT_DATE_PRESET;
    // Resolve the default preset rather than assuming it means today: it is
    // 'this-month', so setting both dates to `new Date()` left the selector
    // reading "This Month" over a range covering only today.
    if (!this.setDatePresetRange(DEFAULT_DATE_PRESET)) {
      this.selectedStartDate = new Date();
      this.selectedEndDate = new Date();
    }
    this.generalSalesFilters = emptyGeneralSalesFilters();
    this.reportError = null;
    // Refresh the full employee list for the dropdown; do not clear options so Filter
    // stays usable and does not show "Loading employees…" on the primary button.
    this.reloadEmployees();
  }

  disableFutureDates = (date: Date): boolean => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date.getTime() > today.getTime();
  };

  private loadShiftReport(): void {
    if (!this.selectedDate) {
      this.reportError = 'Please select a date.';
      return;
    }

    const date = this.toDateString(this.selectedDate);
    this.reportLoading = true;
    this.reportError = null;
    this.cashReport = null;

    this.shiftExportDate = date;
    this.shiftExportLocationIds = [...this.selectedLocationIds];
    this.shiftExportEmployeeIds = [...this.selectedEmployeeIds];

    this.reportsService
      .getShiftReport({
        date,
        location_ids: this.selectedLocationIds,
        employee_ids: this.selectedEmployeeIds,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.reportLoading = false;
        })
      )
      .subscribe({
        next: data => {
          this.shiftReport = data;
        },
        error: err => {
          this.logger.error('Reports: failed to load shift report', err);
          this.shiftReport = null;
          this.reportError =
            err?.error?.message || err?.message || 'Failed to load shift report. Please try again.';
        },
      });
  }

  private loadGeneralSalesReport(): void {
    if (!this.selectedStartDate || !this.selectedEndDate) {
      this.reportError = 'Please select a start and end date.';
      return;
    }

    const startDate = this.toDateString(this.selectedStartDate);
    const endDate = this.toDateString(this.selectedEndDate);
    if (this.inclusiveDayCount(startDate, endDate) < 1) {
      this.reportError = 'End date must be on or after start date.';
      return;
    }

    this.reportLoading = true;
    this.reportError = null;
    this.shiftReport = null;
    this.cashReport = null;

    this.reportsService
      .getGeneralSalesReport({
        start_date: startDate,
        end_date: endDate,
        location_ids: this.selectedLocationIds,
        timezone: this.reportTimezone(),
        compare: this.compareToPreviousPeriod,
        filters: this.generalSalesFilters,
        include_cancelled_orders: true,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.reportLoading = false;
        })
      )
      .subscribe({
        next: data => {
          this.generalSalesReport = data;
          this.trackReport('filters_applied', {
            date_range: `${startDate}..${endDate}`,
            locations: this.selectedLocationIds.length,
            comparison: this.compareToPreviousPeriod,
            filters_applied: this.activeFilterCount,
          });
        },
        error: err => {
          this.logger.error('Reports: failed to load general sales report', err);
          this.generalSalesReport = null;
          this.reportError =
            err?.error?.message ||
            err?.message ||
            'Failed to load general sales report. Please try again.';
        },
      });
  }

  /**
   * The zone the report is actually reckoned in.
   *
   * svc-analytics groups a single-location report by that location's business
   * timezone, so the date range has to be built in the same zone or the two
   * disagree about which day "today" is — the bug that hid a Florida shop's
   * orders from an admin whose browser clock was hours ahead. Matches how
   * analytics, the dashboard and the payments dashboard already resolve it.
   */
  private reportTimezone(): string {
    try {
      return (
        this.locationService.getTimezoneForLocationId(this.selectedLocationIds[0]) ||
        this.browserTimezone()
      );
    } catch {
      return this.browserTimezone();
    }
  }

  /** PRD §31 — admin actions on the General Sales Report. */
  private trackReport(action: string, properties: Record<string, unknown> = {}): void {
    try {
      this.amplitude.trackGeneralSalesReport(action, {
        location_id: this.selectedLocationIds[0] ?? 'all',
        user_role: 'admin',
        ...properties,
      });
    } catch (err) {
      // Tracking must never be able to break the report.
      this.logger.error('Reports: general sales tracking failed', err);
    }
  }

  private browserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private loadCashReport(): void {
    if (!this.selectedStartDate || !this.selectedEndDate) {
      this.reportError = 'Please select a start and end date.';
      return;
    }

    const startDate = this.toDateString(this.selectedStartDate);
    const endDate = this.toDateString(this.selectedEndDate);
    const dayCount = this.inclusiveDayCount(startDate, endDate);
    if (dayCount < 1) {
      this.reportError = 'End date must be on or after start date.';
      return;
    }
    if (dayCount > 31) {
      this.reportError = 'Date range must not exceed 31 days.';
      return;
    }

    this.reportLoading = true;
    this.reportError = null;
    this.shiftReport = null;

    const employeeIds = this.resolveCashEmployeeIds();

    this.cashExportStartDate = startDate;
    this.cashExportEndDate = endDate;
    this.cashExportEmployeeIds = employeeIds;

    this.reportsService
      .getCashReport({
        startDate,
        endDate,
        employee_ids: employeeIds,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.reportLoading = false;
        })
      )
      .subscribe({
        next: data => {
          this.cashReport = data;
        },
        error: err => {
          this.logger.error('Reports: failed to load cash report', err);
          this.cashReport = null;
          this.reportError =
            err?.error?.message || err?.message || 'Failed to load cash report. Please try again.';
        },
      });
  }

  /**
   * Cash report API filters by employee_ids (not location_ids).
   * When locations are selected but no employees, pass employees already filtered by location.
   */
  private resolveCashEmployeeIds(): string[] | undefined {
    if (this.selectedEmployeeIds.length > 0) {
      return this.selectedEmployeeIds;
    }
    if (this.selectedLocationIds.length > 0) {
      return this.employeeOptions.map(opt => opt.id);
    }
    return undefined;
  }

  private inclusiveDayCount(startDate: string, endDate: string): number {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return Number.NaN;
    }
    const ms = end.getTime() - start.getTime();
    return Math.floor(ms / 86400000) + 1;
  }

  private toDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private reloadEmployees(): void {
    const locationIds =
      this.selectedLocationIds.length > 0
        ? this.selectedLocationIds
        : this.locationOptions.map(opt => opt.id);
    this.employeesReload$.next(locationIds);
  }

  private loadLocations(): void {
    this.locationsLoading = true;
    this.locationService
      .getLocations()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.locationsLoading = false;
        })
      )
      .subscribe({
        next: response => {
          if (response?.status && Array.isArray(response.data)) {
            this.locationOptions = this.locationService.getLocationOptions().map(opt => ({
              id: opt.value,
              name: opt.label,
            }));
          } else {
            this.locationOptions = [];
          }
          this.reloadEmployees();
        },
        error: err => {
          this.logger.error('Reports: failed to load locations', err);
          this.locationOptions = [];
          this.employeeOptions = [];
        },
      });
  }
}
